import { FC, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { DirectionalHint, getId, ICalloutContentStyles, Icon, Stack } from "@fluentui/react";
import { debounce, ITheme, useComponentLogger, useTheme } from "cayo.ui";
import DOMPurify from "dompurify";
import { alertDispatcher } from "@cayo/shared/components/Alerts/alertDispatcher";
import {
  NewAlertCallout,
  NewAlertItemContainer,
} from "@cayo/shared/components/Alerts/Alerts.styles";
import hightlightedAlerts from "@cayo/shared/components/Alerts/hightlighted-alerts";
import { IAlertItem, IInteractiveItem } from "@cayo/shared/components/Alerts/types";
import alertUtils from "@cayo/shared/components/Alerts/utils";
import { AppContext } from "@cayo/shared/components/App/appContext";
import { RightPanelType } from "@cayo/shared/components/App/types";
import { animationEffects } from "@cayo/shared/components/common/Effects";
import {
  NotificationAction,
  updateInteractiveItemsReducer,
} from "@cayo/shared/services/notifications";
import {
  AlertPopupDescription,
  AlertPopupTitle,
  AnimationContainer,
  ClearIconButton,
  IconWrapper,
  StyledRow,
} from "./styles";
import RiskIndicator from "../../Graphics/RiskIndicator";

const NewAlertsPopup: FC = () => {
  const log = useComponentLogger(NewAlertsPopup);
  const theme = useTheme();
  const { getColor, getIcon } = theme;
  const descriptionId: string = getId("description");

  const { showRightPanel, firstAlertButtonId } = useContext(AppContext);

  const [alerts, dispatchAlerts] = useReducer(updateInteractiveItemsReducer, [] as IAlertItem[]);

  const [isMouseOver, setIsMouseOver] = useState(false);

  // subscribe to new alerts
  useEffect(() => {
    return alertDispatcher.subscribe("popupAlerts", (data) => {
      if (!data) {
        dispatchAlerts({ newItems: [] });
        hightlightedAlerts.setLastErrorAlert(undefined);
        return;
      }

      if (
        (data?.action === NotificationAction.Remove ||
          data?.action === NotificationAction.Replace) &&
        data?.newItems?.find((i) => hightlightedAlerts.isLastErrorAlert(i as IAlertItem))
      ) {
        hightlightedAlerts.setLastErrorAlert(undefined);
      }
      dispatchAlerts({ ...data!, addNewToTail: true, noSort: true });
    });
  }, []);

  // get an alert to display
  const alert = useMemo(() => {
    const currentAlert = alerts.length && alerts[0];
    if (!currentAlert) {
      log.debug("not alerts to display");
      return undefined;
    }

    log.debug("alert is being displayed", currentAlert);

    return currentAlert as IAlertItem;
  }, [alerts?.length]);

  const isRisk = false; //alert?.objectType === "cayo.graph.riskAlert";

  useEffect(() => {
    const lastAlert = alerts.length ? (alerts[0] as IAlertItem) : undefined;

    const isErrorAlert =
      lastAlert?.severity === "high" ||
      lastAlert?.severity === "error" ||
      lastAlert?.severity === "warning";

    if (isErrorAlert && lastAlert) {
      lastAlert.disappeared = false;
      log.debug("last error alert", lastAlert);
      hightlightedAlerts.setLastErrorAlert(lastAlert);
    }

    if (alert && (alerts.length > 1 || (alerts.length === 1 && !isErrorAlert))) {
      log.debug("alert is being disappeared", lastAlert);

      debounce(() => {
        if (!isMouseOver) {
          log.debug("alert has disappeared", lastAlert);
          dispatchAlerts({
            action: NotificationAction.Remove,
            newItems: [lastAlert as IInteractiveItem],
          });
        } else {
          log.debug("skip alert disappearing", lastAlert);
        }
      }, showPopupTime)();
    }
  }, [alert, alerts?.length, isMouseOver]);

  useEffect(() => {
    if (alerts.length === 0 && hightlightedAlerts.lastErrorAlert) {
      dispatchAlerts({
        action: NotificationAction.Add,
        newItems: [hightlightedAlerts.lastErrorAlert],
        addNewToTail: true,
      });
      hightlightedAlerts.setLastErrorAlert(undefined);
    }
  }, [alerts.length]);

  const icon = useMemo(() => {
    const alertType = alert && alertUtils.getAlertType(alert);
    switch (alertType) {
      case "systemAlerts":
        return "Ringer";
      case "riskAssessment":
        return "LightningBolt";
      default:
        return "Warning";
    }
  }, [alert]);

  if (!firstAlertButtonId) {
    log.debug("no firstAlertButtonId");
    return null;
  }

  return (
    <>
      {alert && (
        <NewAlertCallout
          target={"#" + firstAlertButtonId}
          setInitialFocus={false}
          dismissOnTargetClick={false}
          directionalHint={DirectionalHint.leftCenter}
          // onDismiss={() => setNewAlertsData(undefined)}
          ariaDescribedBy={descriptionId}
          role="alertdialog"
          styles={getStyles(theme)}
          onClick={() => {
            showRightPanel(alertUtils.getAlertType(alert) as RightPanelType);
            hightlightedAlerts.add(alert);

            debounce(() => {
              hightlightedAlerts.resetClickedItem();
            }, 2000)();
          }}
        >
          <NewAlertItemContainer
            onMouseEnter={() => setIsMouseOver(true)}
            onMouseLeave={() => setIsMouseOver(false)}
          >
            <IconWrapper valign nostretch justifyContent="center">
              <Icon iconName={icon} />
            </IconWrapper>
            <AnimationContainer>
              <animationEffects.slideInUp key={alert?.id}>
                <StyledRow valign key={alert.id}>
                  {isRisk ? (
                    // eslint-disable-next-line no-inline-styles/no-inline-styles
                    <RiskIndicator severity={alert?.severity} style={{ marginTop: 2 }} />
                  ) : (
                    <Icon
                      styles={{ root: { color: getColor(alert.severity, "severity") } }}
                      iconName={getIcon(alert.severity, "severity")}
                    />
                  )}
                  <Stack styles={{ root: { marginLeft: 8 } }}>
                    <AlertPopupTitle
                      hasMessage={Boolean(alert.message)}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(alert.subject || ""),
                      }}
                    />
                    {alert.message && (
                      <AlertPopupDescription
                        $linesNumber={1}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(alert.message) }}
                      />
                    )}
                  </Stack>
                </StyledRow>
              </animationEffects.slideInUp>
            </AnimationContainer>
            <ClearIconButton
              styles={{ icon: { fontSize: 10 } }}
              iconProps={{ iconName: "Clear" }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                hightlightedAlerts.setLastErrorAlert(undefined);
                dispatchAlerts({ action: NotificationAction.Remove, newItems: [alerts[0]] });
              }}
              isMouseOver={isMouseOver}
              ariaLabel="Clear alert popup"
            />
          </NewAlertItemContainer>
        </NewAlertCallout>
      )}
    </>
  );
};

const getStyles = (theme: ITheme): Partial<ICalloutContentStyles> => {
  const backgroundColor = theme.officeTheme.semanticColors.bodyStandoutBackground;

  return {
    root: {
      width: 300,
      maxWidth: 400,
      top: "4px !important",
      zIndex: "10000 !important",
    },
    beak: { backgroundColor },
    calloutMain: {
      overflow: "hidden",
      backgroundColor,
      color: theme.officeTheme.semanticColors.bodyText,
    },
  };
};

const showPopupTime = 5000;
export default NewAlertsPopup;
